import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, ExternalLink, Check, User, Quote,
  Boxes, Building2, HeartHandshake, Ban,
  Palette, Layers, Wand2, ShieldCheck,
  ShoppingBag, Briefcase, Crown, Coins, MonitorPlay,
  ShoppingCart,
} from 'lucide-react';
import '@/styles/casestudy.css';
import useReveal from '@/hooks/useReveal';
import { useSeo } from '@/lib/seo';
import { CountUp } from '@/components/CountUp';
import { CaseStudyNav } from '@/components/CaseStudyNav';
import RelatedJournal from '@/components/RelatedJournal';
import { ScrollProgress } from '@/components/ScrollProgress';
import { BackToTop } from '@/components/BackToTop';
import {
  TS_IMAGES, BOOST_LOGO, TS_HERO, TS_CLIENT, TS_CHALLENGE, TS_APPROACH,
  TS_SOLUTION, TS_RESULTS, TS_TECH, TS_TESTIMONIAL, TS_CTA,
} from '@/data/templeCaseStudy';

const CANONICAL = 'https://boostmarketing.agency/case-studies/temple-of-sound';
const SEO_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Two legends, one house — Temple of Sound case study',
    description:
      "How Boost! designed and built a 100% bespoke full-stack retail platform for Temple of Sound — B2C shop, B2B trade portal, membership and a dual loyalty engine.",
    image: TS_IMAGES.hero,
    author: { '@type': 'Organization', name: 'BOOST! Design & Marketing', url: 'https://boostmarketing.agency/' },
    publisher: { '@type': 'Organization', name: 'BOOST! Design & Marketing', logo: { '@type': 'ImageObject', url: BOOST_LOGO } },
    about: { '@type': 'Organization', name: 'Temple of Sound Distribution', url: 'https://www.templeofsound.co.uk' },
    keywords: 'ecommerce web design, full-stack build, B2B trade portal, membership, loyalty engine, Stripe, drum retail, cymbals, drumsticks',
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boostmarketing.agency/' },
      { '@type': 'ListItem', position: 2, name: 'Work', item: 'https://boostmarketing.agency/work' },
      { '@type': 'ListItem', position: 3, name: 'Temple of Sound', item: CANONICAL },
    ],
  },
];

const ICONS = {
  Boxes, Building2, HeartHandshake, Ban, Palette, Layers, Wand2, ShieldCheck,
  ShoppingBag, Briefcase, Crown, Coins, MonitorPlay, ShoppingCart,
};

function Reveal({ children, className = '', delay = 0, as: Tag = 'div', ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`cs-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

function Hero() {
  return (
    <header className="cs-hero" data-testid="ts-hero">
      <div className="cs-container">
        <Reveal className="cs-hero-eyebrow" as="span"><span className="cs-dot" /> {TS_HERO.eyebrow}</Reveal>
        <Reveal>
          <h1 className="cs-hero-title" data-testid="ts-hero-title">
            Two legends, <span className="cs-hl">one house</span>
          </h1>
        </Reveal>
        <Reveal delay={80}><p className="cs-hero-sub">{TS_HERO.sub}</p></Reveal>
        <Reveal delay={140} className="cs-hero-meta">
          <div className="cs-chips">
            {TS_HERO.chips.map((c) => <span className="cs-chip" key={c}>{c}</span>)}
          </div>
          <span className="cs-year">{TS_HERO.year}</span>
        </Reveal>
        <Reveal delay={180}>
          <a className="cs-hero-live" href={TS_HERO.liveUrl} target="_blank" rel="noopener noreferrer" data-testid="ts-hero-live-link">
            <span className="cs-client-tag">Client:</span> {TS_HERO.client}
            <ExternalLink size={16} />
          </a>
        </Reveal>
        <Reveal delay={120} className="cs-hero-figure">
          <div className="cs-frame">
            <div className="cs-frame-bar">
              <i /><i /><i />
              <span className="cs-frame-url">templeofsound.co.uk</span>
            </div>
            <img src={TS_IMAGES.hero} alt="Temple of Sound — cinematic retail homepage" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </header>
  );
}

function ClientSection() {
  return (
    <section className="cs-section" data-testid="ts-client">
      <div className="cs-container">
        <div className="cs-client-grid">
          <Reveal>
            <span className="cs-kicker">{TS_CLIENT.kicker}</span>
            <h2 className="cs-h2">{TS_CLIENT.title}</h2>
          </Reveal>
          <Reveal delay={100}>
            {TS_CLIENT.copy.map((p) => (
              <p className="cs-lead" key={p.slice(0, 20)} style={{ marginTop: 0, marginBottom: 18 }}>{p}</p>
            ))}
            <div className="cs-facts">
              {TS_CLIENT.facts.map((f) => (
                <div className="cs-fact" key={f.value}>
                  <div className="cs-fact-value">{f.value}</div>
                  <div className="cs-fact-label">{f.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function IconGrid({ data, testid, sectionClass = '', numbered = false }) {
  return (
    <section className={`cs-section ${sectionClass}`} data-testid={testid}>
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{data.kicker}</span>
          <h2 className="cs-h2">{data.title}</h2>
        </Reveal>
        <div className={numbered ? 'cs-approach-grid' : 'cs-challenge-grid'}>
          {data.items.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal className={numbered ? 'cs-approach-card' : 'cs-challenge-card'} key={item.title} delay={i * 70}>
                {numbered && <span className="cs-approach-num">{String(i + 1).padStart(2, '0')}</span>}
                <span className="cs-x-icon"><Icon size={22} /></span>
                <h3 className="cs-card-title">{item.title}</h3>
                <p className="cs-card-copy">{item.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SolutionSection() {
  const s = TS_SOLUTION;
  return (
    <section className="cs-section cs-section-alt" data-testid="ts-solution">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{s.kicker}</span>
          <h2 className="cs-h2">{s.title}</h2>
        </Reveal>

        <div className="cs-feature-grid" style={{ marginTop: 40 }}>
          {s.features.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <Reveal className="cs-feature-card" key={f.title} delay={(i % 3) * 70}>
                <span className="cs-feature-icon"><Icon size={22} /></span>
                <h3 className="cs-card-title">{f.title}</h3>
                <p className="cs-card-copy">{f.copy}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="cs-sol-label" as="div">{s.gallery.label}</Reveal>
        <div className="cs-gallery">
          {s.gallery.shots.map((shot, i) => (
            <Reveal className="cs-gallery-item" key={shot.caption} delay={i * 80}>
              <img src={shot.src} alt={shot.caption} loading="lazy" />
              <span className="cs-gallery-cap">{shot.caption}</span>
            </Reveal>
          ))}
        </div>

        <Reveal className="cs-sol-label" as="div">Two bespoke loyalty currencies</Reveal>
        <div className="cs-loyalty">
          {s.loyalty.map((l, i) => (
            <Reveal className="cs-loyalty-card" key={l.name} delay={i * 80}>
              <img src={l.src} alt={l.name} loading="lazy" />
              <div>
                <div className="cs-loyalty-name">{l.name}</div>
                <div className="cs-loyalty-desc">{l.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ResultsSection() {
  const r = TS_RESULTS;
  return (
    <section className="cs-section" data-testid="ts-results">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{r.kicker}</span>
          <h2 className="cs-h2">{r.title}</h2>
        </Reveal>
        <div className="cs-stats">
          {r.headline.map((stat, i) => (
            <Reveal className="cs-stat" key={stat.label} delay={i * 70}>
              <div className="cs-stat-value"><CountUp value={stat.value} />{stat.unit && <span className="cs-unit">{stat.unit}</span>}</div>
              <div className="cs-stat-label">{stat.label}</div>
              <div className="cs-stat-note">{stat.note}</div>
            </Reveal>
          ))}
        </div>
        <div className="cs-breakdown">
          {r.groups.map((g, i) => {
            const Icon = ICONS[g.icon];
            return (
              <Reveal className="cs-bd-card" key={g.title} delay={i * 80}>
                <div className="cs-bd-head">
                  <span className="cs-x-icon" style={{ marginBottom: 0, width: 40, height: 40 }}><Icon size={19} /></span>
                  <h4>{g.title}</h4>
                </div>
                <ul className="cs-bd-list">
                  {g.points.map((p) => <li key={p.slice(0, 20)}><Check size={15} /> <span>{p}</span></li>)}
                </ul>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TechStrip() {
  return (
    <section className="cs-tech" data-testid="ts-tech">
      <div className="cs-container">
        <Reveal>
          <div className="cs-tech-label">Tech &amp; capabilities</div>
          <div className="cs-tech-strip">
            {TS_TECH.map((t) => <span className="cs-tech-item" key={t}>{t}</span>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonial() {
  const t = TS_TESTIMONIAL;
  return (
    <section className="cs-section" data-testid="ts-testimonial" style={{ paddingBottom: 'clamp(40px, 6vw, 70px)' }}>
      <div className="cs-container">
        <Reveal className="cs-quote">
          <Quote className="cs-quote-mark" size={64} strokeWidth={0} fill="currentColor" style={{ opacity: 0.35 }} />
          <p className="cs-quote-text">{t.quote}</p>
          <div className="cs-quote-author">
            {t.photo
              ? <img className="cs-quote-photo" src={t.photo} alt={t.name} />
              : <span className="cs-quote-photo-empty"><User size={24} /></span>}
            <div>
              <div className="cs-quote-name">{t.name}</div>
              <div className="cs-quote-role">{t.role}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ClosingCta() {
  const c = TS_CTA;
  return (
    <section className="cs-cta-section" data-testid="ts-cta">
      <div className="cs-container">
        <Reveal>
          <h2 className="cs-cta-title">{c.title}</h2>
          <p className="cs-cta-sub">{c.sub}</p>
          <div className="cs-cta-btns">
            <a className="cs-btn cs-btn-primary" href={`mailto:${c.email}`} data-testid="ts-cta-email">
              Start a project <ArrowUpRight size={18} />
            </a>
            <a className="cs-btn cs-btn-ghost" href={c.siteUrl} target="_blank" rel="noopener noreferrer" data-testid="ts-cta-site">
              Visit boostmarketing.agency <ExternalLink size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CaseStudyFooter() {
  return (
    <footer className="cs-footer" data-testid="ts-footer">
      <div className="cs-container cs-footer-inner">
        <div className="cs-footer-brand">
          <img src={BOOST_LOGO} alt="Boost! Design & Marketing" />
          <div className="cs-footer-credit">
            Designed &amp; built by <strong>Boost! Design &amp; Marketing Ltd</strong>
            <br />© {new Date().getFullYear()} — Giving your brand lift-off.
          </div>
        </div>
        <a className="cs-footer-link" href="https://www.boostmarketing.agency" target="_blank" rel="noopener noreferrer" data-testid="ts-footer-link">
          www.boostmarketing.agency <ArrowUpRight size={15} />
        </a>
      </div>
    </footer>
  );
}

export default function CaseStudyTemple() {
  useSeo({
    title: 'Temple of Sound Case Study — Bespoke Retail Platform | Boost!',
    description:
      "How Boost! built a 100% bespoke full-stack retail platform for Temple of Sound — B2C shop, B2B trade portal, three-tier membership, dual loyalty engine and a custom drumstick builder.",
    canonical: CANONICAL,
    image: 'https://boostmarketing.agency/og-temple.png',
    jsonLd: SEO_JSONLD,
  });
  return (
    <div className="cs-page cs-temple" data-testid="case-study-temple">
      <div className="cs-grain" />
      <div className="cs-glows" />
      <ScrollProgress />
      <Link to="/work" className="cs-back" data-testid="ts-back-to-work">
        <ArrowLeft size={16} /> Back to work
      </Link>
      <main>
        <Hero />
        <ClientSection />
        <IconGrid data={TS_CHALLENGE} testid="ts-challenge" sectionClass="cs-section-alt" />
        <IconGrid data={TS_APPROACH} testid="ts-approach" numbered />
        <SolutionSection />
        <ResultsSection />
        <TechStrip />
        <Testimonial />
        <ClosingCta />
        <RelatedJournal caseStudy="temple-of-sound" />
        <CaseStudyNav
          to="/case-studies/diamondbrite"
          title="From 2.8★ to 4.9★"
          client="Diamondbrite · Reputation, ecommerce & marketing"
        />
      </main>
      <CaseStudyFooter />
      <BackToTop />
    </div>
  );
}
